import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Facebook, Loader2, Send, ExternalLink, ChevronDown, Info, Clock,
  Megaphone, Eye, Move, RotateCcw,
} from "lucide-react";
import { api, BACKEND_BASE } from "@/lib/api";
import PhotoStage, { useImageAspect } from "@/components/PhotoStage";
import FacebookPhotoPicker from "@/components/FacebookPhotoPicker";
import { initialPhotos, photoPaths } from "@/lib/fbPhotos";
import { MAX_ZOOM, MIN_ZOOM, manualRect, rectToPlacement } from "@/lib/carouselLayout";
import { ScheduleControls } from "@/components/ScheduledPostsList";
import { formatInTz, propertyTimezone } from "@/lib/timezone";
import { tr } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

/**
 * Facebook publishing panel for a single article.
 *
 * Two modes (per the editor's request):
 *   - "link":  post the article URL with the LLM-authored FB copy — Meta
 *              generates the OpenGraph preview from the URL.
 *   - "photo": post one or more photos (up to 10, featured image first
 *              by default — see FacebookPhotoPicker) with the FB copy in
 *              the caption; no expanded link preview. The article URL, if
 *              wanted, lives inline in the caption or the first comment.
 *
 * The user picks which connected Page to publish to. Instagram is handled
 * separately inside StoryCardPanel because it needs the rendered card.
 */
// Extract the storage path (e.g. "ggn/generated/abc.png") from a
// "/api/files/<path>" URL. Returns "" if the article has no featured
// image or the URL doesn't match the expected shape.
function storagePathFromUrl(url) {
  if (!url) return "";
  const marker = "/api/files/";
  const i = url.indexOf(marker);
  if (i < 0) return "";
  return url.slice(i + marker.length);
}

export default function FacebookSharePanel({ article }) {
  const { data: connData, isLoading: connLoading } = useQuery({
    queryKey: ["social-connections"],
    queryFn: api.listSocialConnections,
  });
  // Fetch property settings so we can preview the exact teaser + comment
  // copy that will hit Facebook when "Share link in the first comment"
  // is enabled. Backend applies its own defaults if these are blank, so
  // an unset value here just means "showing the default".
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: api.getSettings,
  });
  const teaserSuffix =
    (settings?.fb_link_in_comment_teaser_suffix || "").trim() ||
    "\uD83D\uDC47 Link in the comments!";
  const commentPrefix =
    (settings?.fb_link_in_comment_prefix || "").trim() ||
    "\uD83D\uDC49 Read the full story here:";
  // Hidden pages stay connected server-side but shouldn't appear in the
  // share dropdown — they're the editor's way of keeping "Cabo" pages
  // off the "North" property, etc.
  const pages = useMemo(
    () => (connData?.items || []).filter((p) => !p.hidden),
    [connData],
  );
  // The Page the editor pinned as this property's default, resolved
  // server-side (see backend/social_pages.py) so a default that has
  // since been hidden or disconnected steps down to a pickable Page.
  // The `pages[0]` tail is belt-and-braces for a cached response from
  // before the field existed.
  const defaultPageId = connData?.default_page_id || pages[0]?.page_id || "";

  const imagePath = storagePathFromUrl(article.generated_image_url);
  const hasFeaturedImage = !!imagePath;
  // Photo-post photos in post order (lib/fbPhotos.js). Starts with the
  // featured image; the first photo is the one the sponsor ad goes on.
  const [photos, setPhotos] = useState(() => initialPhotos(article));
  // A featured image set or swapped while the panel is open follows
  // into the list: it takes the old featured image's place, or starts
  // an empty list. Photos the editor picked are left alone.
  const featuredUrlRef = useRef(article.generated_image_url || "");
  useEffect(() => {
    const next = article.generated_image_url || "";
    const prev = featuredUrlRef.current;
    if (next === prev) return;
    featuredUrlRef.current = next;
    setPhotos((cur) => {
      const nextList = initialPhotos(article);
      if (cur.length === 0) return nextList;
      const at = cur.findIndex((p) => p.url === prev);
      if (at < 0) return cur;
      const rest = cur.filter((_, i) => i !== at);
      if (!nextList.length || rest.some((p) => p.url === next)) return rest;
      return [...rest.slice(0, at), nextList[0], ...rest.slice(at)];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article.generated_image_url]);
  const firstPhotoUrl = photos[0]?.url || "";
  const firstPhotoPath = storagePathFromUrl(firstPhotoUrl);

  // Detect whether the WordPress post itself is scheduled for the
  // future. If so, we pre-set the FB share to also schedule — the
  // property's `social_cascade_delay_minutes` (default 15) AFTER the
  // WP publish so the article is live when the FB link resolves.
  const cascadeDelayMin =
    Number.isFinite(Number(settings?.social_cascade_delay_minutes))
      ? Number(settings.social_cascade_delay_minutes)
      : 15;
  const wpScheduledAt = article.wordpress_scheduled_date || "";
  const wpScheduledInFuture =
    !!wpScheduledAt && new Date(wpScheduledAt).getTime() > Date.now();
  // Return a proper UTC ISO — `ScheduleControls` stores the value as
  // a UTC ISO throughout its lifecycle, so we must NOT hand it a
  // naive wall-clock here (that would confuse the input rendering
  // AND — historically — get double-converted by the mutation and
  // land as "" on the server, silently publishing immediately).
  const cascadeIso = useMemo(() => {
    if (!wpScheduledInFuture) return "";
    const t = new Date(wpScheduledAt).getTime() + cascadeDelayMin * 60 * 1000;
    return new Date(t).toISOString();
  }, [wpScheduledAt, wpScheduledInFuture, cascadeDelayMin]);

  const [pageId, setPageId] = useState("");
  // `connData` is undefined on the first render (react-query resolves
  // async), so `pageId` can't be seeded from useState — it would freeze
  // at "". Seed it once the list arrives. The `!pageId` guard means a
  // refetch never stomps a Page the editor picked by hand.
  useEffect(() => {
    if (!pageId && defaultPageId) setPageId(defaultPageId);
  }, [defaultPageId, pageId]);
  // Default to PHOTO mode — the newsroom's house preference for
  // organic reach. Fall back to link if there's no image (photo mode
  // needs one).
  const [mode, setMode] = useState(hasFeaturedImage ? "photo" : "link");
  const [caption, setCaption] = useState(article.facebook_post || "");
  // Follow the article's Facebook copy when it changes underneath us
  // (e.g. an accepted fact-check fix), but only while the editor hasn't
  // typed their own caption here — a hand-edited caption is left alone.
  const seededCaptionRef = useRef(article.facebook_post || "");
  useEffect(() => {
    const next = article.facebook_post || "";
    setCaption((cur) => (cur === seededCaptionRef.current ? next : cur));
    seededCaptionRef.current = next;
  }, [article.facebook_post]);
  const [articleUrl, setArticleUrl] = useState(article.wordpress_link || "");
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledFor, setScheduledFor] = useState("");
  // Bumped after every successful SCHEDULED send. A property with more
  // than one Page publishes the same article to each Page in turn, and
  // every one of them has to inherit the WordPress cascade. The old
  // `onSuccess` cleared `scheduleEnabled` outright, so Page 2 onward
  // silently fell back to publish-now — the link would go out before
  // WordPress had made the article live.
  const [cascadeArm, setCascadeArm] = useState(0);
  // Remembers the last value the cascade auto-filled. If the field
  // still holds exactly that, the editor hasn't touched it and we're
  // free to overwrite (which matters because `settings` — and with it
  // the property's real cascade delay — arrives AFTER the first render,
  // so the first auto-fill uses the 15-minute fallback). If it differs,
  // the editor typed their own time and we leave it alone.
  const lastAutoIsoRef = useRef("");
  // Arm the cascade: on mount, whenever the WP schedule or the cascade
  // delay resolves, and again after each scheduled send.
  useEffect(() => {
    if (!cascadeIso) return;
    const prevAuto = lastAutoIsoRef.current;
    lastAutoIsoRef.current = cascadeIso;
    setScheduleEnabled(true);
    setScheduledFor((prev) => (!prev || prev === prevAuto ? cascadeIso : prev));
  }, [cascadeIso, article.id, cascadeArm]);
  // "Share link in the first comment" — Meta caps reach on link posts,
  // so many pages get better organic engagement by putting the URL in
  // the first comment. Meaningful in Photo mode only (Link mode already
  // has the URL as the post's main surface). Default ON per the
  // newsroom's house preference.
  const [linkInFirstComment, setLinkInFirstComment] = useState(true);
  // --- Sponsor Ad overlay state ---------------------------------------
  // Pre-seed from the article's last-used ad+style so repeat publishes
  // don't lose the editor's previous selection.  Background mode and
  // element layout come from the ad's saved styles now — the editor
  // configures those on /sponsor-ads, so the FB panel just picks
  // (ad, style) and everything else is baked in.
  const [adId, setAdId] = useState(article.last_fb_ad_id || "");
  const [adStyle, setAdStyle] = useState(article.last_fb_ad_style || "bottom_banner");
  const { data: ads = [] } = useQuery({
    queryKey: ["ads"],
    queryFn: () => api.listAds(),
  });
  // Preview state — restored so editors can eyeball the real featured
  // image with the chosen ad+style before publishing. Background mode
  // + element positions come from the ad's saved styles now, so this
  // is a one-click render, not a full config panel.
  const [previewUrl, setPreviewUrl] = useState(null);
  // Photo placement under the ad ({zoom, x, y} in canvas widths — see
  // backend/photo_placement.py). null = the photo as it is. Seeded from
  // the last share only when it was made on this same photo.
  const [photoPlacement, setPhotoPlacement] = useState(() => {
    const last = article.last_fb_photo_placement;
    return last && last.image === imagePath && last.zoom > 0
      ? { zoom: last.zoom, x: last.x || 0, y: last.y || 0 }
      : null;
  });
  const previewMutation = useMutation({
    mutationFn: () =>
      api.fetchFacebookAdPreview(article.id, {
        ad_id: adId, style: adStyle,
        photo_placement: photoPlacement || undefined,
        image_path: firstPhotoPath || undefined,
      }),
    onSuccess: (blob) => {
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || tr("Preview failed")),
  });
  // The placement and the preview belong to the first photo. When a
  // different photo moves into first place, both start over.
  const placedPhotoRef = useRef(firstPhotoPath);
  useEffect(() => {
    if (placedPhotoRef.current === firstPhotoPath) return;
    placedPhotoRef.current = firstPhotoPath;
    setPhotoPlacement(null);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, [firstPhotoPath]);
  const qc = useQueryClient();
  // Which Pages already have a Facebook post queued for this article.
  // With several Pages in play there's otherwise nothing on screen to
  // say where you've already been — you have to scroll down to the
  // Scheduled posts list and cross-reference by name. Same query key as
  // ScheduledPostsList, so the invalidation after each send refreshes
  // both at once.
  const { data: queuedData } = useQuery({
    queryKey: ["scheduled-posts", article.id],
    queryFn: () => api.listScheduledPosts({ article_id: article.id }),
  });
  const queuedPageIds = useMemo(() => {
    const ids = new Set();
    for (const post of queuedData?.items || []) {
      if (post.platform === "facebook" && post.status === "pending") {
        ids.add(post.page_id);
      }
    }
    return ids;
  }, [queuedData]);

  // Keep sensible defaults if the initially-selected page vanishes.
  const activePage = useMemo(
    () =>
      pages.find((p) => p.page_id === pageId) ||
      pages.find((p) => p.page_id === defaultPageId) ||
      pages[0],
    [pages, pageId, defaultPageId],
  );

  const share = useMutation({
    mutationFn: () =>
      api.shareToFacebook(article.id, {
        page_id: activePage.page_id,
        mode,
        caption,
        // article_url is required whenever the audience needs the link
        // click surface — either the Link-mode preview OR the first
        // comment we auto-append in photo mode.
        article_url:
          mode === "link" || (mode === "photo" && linkInFirstComment)
            ? articleUrl
            : undefined,
        // Photo mode: every photo in post order. `image_path` (the first)
        // is kept for older backends; `image_paths` wins when present.
        image_path: mode === "photo" ? firstPhotoPath || undefined : undefined,
        image_paths: mode === "photo" ? photoPaths(photos) : undefined,
        // `scheduledFor` is already a UTC ISO — `ScheduleControls`
        // converts the datetime-local wall-clock to UTC on change,
        // so we send it straight through. Do NOT wrap in another
        // `localInputToUtcIso` — that produced empty string and
        // caused instant-publish (see git blame).
        scheduled_for: scheduleEnabled ? scheduledFor : undefined,
        link_in_first_comment: mode === "photo" ? linkInFirstComment : false,
        // Sponsor-ad overlay — only sent when the editor picked one AND
        // we're in photo mode (ads have no meaning on link posts). The
        // saved layout on the ad (including background mode + element
        // positions) controls everything else.
        ad_id: mode === "photo" && adId ? adId : undefined,
        ad_style: mode === "photo" && adId ? adStyle : undefined,
        photo_placement: mode === "photo" && adId && photoPlacement ? photoPlacement : undefined,
      }),
    onSuccess: (r) => {
      if (r.scheduled) {
        toast.success(
          tr("Scheduled for {when} on {page_name}", { when: formatInTz(
            r.post.scheduled_for,
            propertyTimezone(settings),
          ), page_name: activePage.page_name }),
        );
        // Re-arm instead of clearing. On a multi-Page property the
        // editor's next move is to switch Pages and schedule again, and
        // that Page needs the same cascade. `cascadeArm` re-runs the
        // arming effect, which keeps a hand-edited time if there is one.
        if (cascadeIso) {
          setCascadeArm((n) => n + 1);
        } else {
          // No WordPress schedule to cascade from — this was a one-off
          // manual time, so fall back to the old clear-after-send.
          setScheduleEnabled(false);
        }
        qc.invalidateQueries({ queryKey: ["scheduled-posts", article.id] });
      } else {
        toast.success(
          r.photo_count > 1
            ? tr("Posted {photo_count} photos to {page_name} — Facebook post id {fb_post_id}", { photo_count: r.photo_count, page_name: activePage.page_name, fb_post_id: r.fb_post_id })
            : tr("Posted to {page_name} — Facebook post id {fb_post_id}", { page_name: activePage.page_name, fb_post_id: r.fb_post_id }),
        );
        // If the comment step failed but the post itself is live,
        // surface it as a WARNING rather than a hard error so the
        // editor knows what happened.
        if (r.first_comment_error) {
          toast.warning(
            tr("Post is live but the first-comment step failed: {first_comment_error}", { first_comment_error: r.first_comment_error }),
          );
        }
        qc.invalidateQueries({ queryKey: ["article", article.id] });
        qc.invalidateQueries({ queryKey: ["articles"] });
      }
    },
    onError: (e) =>
      toast.error(
        e?.response?.data?.detail || e?.message || tr("Publish failed"),
      ),
  });

  if (connLoading) return null;

  if (pages.length === 0) {
    return (
      <section
        data-testid="facebook-share-panel-empty"
        className="mb-6 border border-[var(--border)] bg-white p-4"
      >
        <div className="flex items-center gap-2 text-sm text-[var(--ink-muted)]">
          <Info className="w-3.5 h-3.5" />{trRich("Connect a Facebook Page in <0>Settings</0> to publish directly from here.", null, [<a
            href="/settings"
            className="underline hover:text-[var(--ink)]" />])}</div>
      </section>
    );
  }

  const modes = [
    {
      key: "link",
      label: tr("Link post"),
      hint: tr("Facebook auto-generates the preview from the article URL."),
    },
    {
      key: "photo",
      label: tr("Photo post"),
      hint: tr("Posts up to 10 photos + caption. URL lives inside the copy or the first comment — no link preview."),
    },
  ];

  const canPublish =
    !!activePage &&
    caption.trim().length > 0 &&
    (mode === "photo"
      ? photos.length > 0 &&
        (!linkInFirstComment || (articleUrl || "").trim().length > 0)
      : (articleUrl || "").trim().length > 0) &&
    !share.isPending;

  return (
    <section
      data-testid="facebook-share-panel"
      className="mb-6 bg-white border border-[var(--border)]"
    >
      <div className="px-5 py-3 border-b border-[var(--border)] flex items-center gap-2">
        <Facebook className="w-3.5 h-3.5 text-[#1877F2]" />
        {trRich("<0>Publish to Facebook</0>", null, [<span className="overline" />])}
      </div>

      <div className="p-5 space-y-4">
        {/* Page picker */}
        <div>
          <label className="overline mb-1.5 block">{tr("Page")}</label>
          <div className="relative inline-flex">
            <select
              data-testid="fb-share-page-select"
              value={pageId || activePage?.page_id || ""}
              onChange={(e) => setPageId(e.target.value)}
              className="appearance-none pr-8 pl-3 py-1.5 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
            >
              {pages.map((p) => (
                <option key={p.page_id} value={p.page_id}>
                  {p.page_name}
                  {queuedPageIds.has(p.page_id) ? tr("  ✓ queued") : ""}
                </option>
              ))}
            </select>
            <ChevronDown className="w-3.5 h-3.5 text-[var(--ink-muted)] absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>

        {/* Mode selector */}
        <div>
          <label className="overline mb-1.5 block">{tr("Mode")}</label>
          <div className="flex flex-wrap gap-2">
            {modes.map((m) => (
              <button
                key={m.key}
                data-testid={`fb-share-mode-${m.key}`}
                disabled={m.disabled}
                onClick={() => setMode(m.key)}
                className={
                  "text-xs px-2.5 py-1 border disabled:opacity-40 disabled:cursor-not-allowed " +
                  (mode === m.key
                    ? "border-[#1877F2] bg-[#1877F2] text-white"
                    : "border-[var(--border)] hover:border-[var(--ink)]")
                }
                title={m.hint + (m.disabled ? tr(" (no featured image)") : "")}
              >
                {m.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-[var(--ink-muted)] mt-1.5">
            {modes.find((m) => m.key === mode)?.hint}
          </p>
        </div>

        {/* Article URL — only for link mode */}
        {mode === "link" && (
          <div>
            <label className="overline mb-1.5 block">{tr("Article URL")}</label>
            <input
              data-testid="fb-share-article-url"
              type="url"
              value={articleUrl}
              onChange={(e) => setArticleUrl(e.target.value)}
              placeholder="https://ggnorth.com/2026/…"
              className="w-full px-3 py-1.5 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
            />
            {!articleUrl && (
              <p className="text-[11px] text-amber-700 mt-1">{tr("Publish to WordPress first to get a permalink, or paste one.")}</p>
            )}
          </div>
        )}

        {/* Photos — photo mode only: pick, add and order up to 10. */}
        {mode === "photo" && (
          <FacebookPhotoPicker article={article} photos={photos} setPhotos={setPhotos} />
        )}

        {/* Sponsor ad picker — photo mode only (ads have no meaning on
            link posts, where the image is auto-generated by Meta from
            the article URL). Goes on the FIRST photo. Shows dropdown →
            style grid → bg toggle → live preview. */}
        {mode === "photo" && firstPhotoPath && (
          <SponsorAdPicker
            ads={ads}
            adId={adId} setAdId={setAdId}
            adStyle={adStyle} setAdStyle={setAdStyle}
            previewUrl={previewUrl}
            previewMutation={previewMutation}
            article={article}
            photoUrl={firstPhotoUrl}
            photoPath={firstPhotoPath}
            multiPhoto={photos.length > 1}
            photoPlacement={photoPlacement}
            setPhotoPlacement={setPhotoPlacement}
          />
        )}

        {/* Caption */}
        <div>
          <label className="overline mb-1.5 block">{tr("Caption")}</label>
          <textarea
            data-testid="fb-share-caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            rows={6}
            className="w-full px-3 py-2 text-sm resize-y border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
            placeholder={tr("The Facebook copy from the LLM will appear here — edit before posting if you want.")}
          />
          <div className="flex items-center justify-between text-[11px] text-[var(--ink-muted)] mt-1">
            {trRich("<0>{captionCount} chars</0>", { captionCount: caption.length }, [<span />])}
            {mode === "photo" && !linkInFirstComment && (
              <span className="italic">{tr("Tip: paste the article URL inside the caption if you want readers to click through.")}</span>
            )}
          </div>
        </div>

        {/* Share link in the first comment — photo mode only, since
            in Link mode the URL is already the post's main surface. */}
        {mode === "photo" && (
          <div className="border-t border-[var(--border)] pt-4 space-y-3">
            <label
              className="flex items-start gap-2 text-sm cursor-pointer select-none"
              data-testid="fb-link-in-comment-toggle-label"
            >
              <input
                type="checkbox"
                data-testid="fb-link-in-comment-toggle"
                checked={linkInFirstComment}
                onChange={(e) => setLinkInFirstComment(e.target.checked)}
                className="mt-0.5 accent-[#1877F2]"
              />
              {trRich("<0><1>Share link in the first comment</1><2>Boosts organic reach — Facebook down-ranks posts with external links in the body. We append a teaser to the caption and auto-post the URL as the first comment.</2></0>", null, [<span />, <span className="font-medium" />, <span className="block text-[11px] text-[var(--ink-muted)] mt-0.5" />])}
            </label>

            {linkInFirstComment && (
              <div className="pl-6 space-y-3">
                <div>
                  <label className="overline mb-1.5 block">{tr("Article URL")}</label>
                  <input
                    data-testid="fb-share-article-url-photo"
                    type="url"
                    value={articleUrl}
                    onChange={(e) => setArticleUrl(e.target.value)}
                    placeholder="https://ggnorth.com/2026/…"
                    className="w-full px-3 py-1.5 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
                  />
                  {!articleUrl && (
                    <p className="text-[11px] text-amber-700 mt-1">{tr("Publish to WordPress first to get a permalink, or paste one.")}</p>
                  )}
                </div>

                {/* Preview — shows the editor exactly what will hit
                    Facebook so there's no "magic" behaviour. Copy is
                    pulled from the property settings, with the shipped
                    defaults as fallback. */}
                <div
                  data-testid="fb-link-in-comment-preview"
                  className="text-xs bg-[var(--secondary)] border border-[var(--border)] p-2.5"
                >
                  <div className="overline mb-1 text-[10px]">{tr("Preview · post")}</div>
                  <div className="whitespace-pre-wrap break-words text-[var(--ink)]">
                    <span className="text-[var(--ink-muted)]">…{caption.slice(-60) || tr("your caption")}</span>
                    {"\n\n"}
                    <strong>{teaserSuffix}</strong>
                  </div>
                  <div className="overline mt-2 mb-1 text-[10px]">{tr("Preview · first comment")}</div>
                  <div className="whitespace-pre-wrap break-words text-[var(--ink)]">
                    <strong>{commentPrefix}</strong>{" "}
                    {articleUrl ? (
                      <span className="underline">{articleUrl}</span>
                    ) : (
                      <span className="text-amber-700 italic">{tr("(article URL required)")}</span>
                    )}
                  </div>
                  <div className="text-[10px] text-[var(--ink-muted)] mt-2">{trRich("Edit these phrases in <0>Settings</0> · Facebook copy.", null, [<a
                      href="/settings"
                      className="underline hover:text-[var(--ink)]" />])}</div>
                </div>
              </div>
            )}
          </div>
        )}

        <ScheduleControls
          enabled={scheduleEnabled}
          setEnabled={setScheduleEnabled}
          isoValue={scheduledFor}
          setIsoValue={setScheduledFor}
        />

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            data-testid="fb-share-publish"
            onClick={() => share.mutate()}
            disabled={
              !canPublish ||
              (scheduleEnabled && !scheduledFor)
            }
            className={
              "inline-flex items-center gap-2 px-3 py-1.5 text-sm text-white disabled:opacity-50 disabled:cursor-not-allowed " +
              (scheduleEnabled
                ? "bg-amber-700 hover:bg-amber-800"
                : "bg-[#1877F2] hover:bg-[#166dd8]")
            }
          >
            {share.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : scheduleEnabled ? (
              <Clock className="w-4 h-4" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            {share.isPending
              ? scheduleEnabled
                ? tr("Scheduling…")
                : tr("Publishing…")
              : scheduleEnabled
              ? tr("Schedule for {page_name}", { page_name: activePage?.page_name || tr("Facebook") })
              : tr("Publish to {page_name}", { page_name: activePage?.page_name || tr("Facebook") })}
          </button>
          {share.data?.fb_post_id && (
            <a
              href={`https://www.facebook.com/${share.data.fb_post_id}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-[var(--ink-muted)] hover:text-[var(--ink)]"
            >{tr("View post")}<ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </section>
  );
}


// --- Sponsor ad picker sub-component -------------------------------------
// Ad dropdown + 6-style visual grid + solid/gradient toggle + preview.
// Kept in this file (not extracted) because it consumes the same
// `article` context (via props) and is only ever rendered inside the
// FB share panel — no reuse pending.
const AD_STYLES = [
  { key: "bottom_banner",     get label() { return tr("1. Bottom Banner"); },     get hint() { return tr("Full-width strip at the bottom."); } },
  { key: "top_banner",        get label() { return tr("2. Top Banner"); },        get hint() { return tr("Full-width strip at the top."); } },
  { key: "corner_badge",      get label() { return tr("3. Corner Badge"); },      get hint() { return tr("Top-right poster badge."); } },
  { key: "bottom_bar_semi",   get label() { return tr("4. Bottom Bar (semi)"); }, get hint() { return tr("Semi-transparent bottom bar."); } },
  { key: "side_vertical",     get label() { return tr("5. Side Vertical"); },     get hint() { return tr("Vertical strip on the right."); } },
  { key: "bottom_left_badge", get label() { return tr("6. Bottom Left Badge"); }, get hint() { return tr("Bottom badge + contact bar."); } },
];

const PLACE_W = 1000;        // stage content width (canvas widths are unit-free)
const PLACE_MARGIN = 250;    // content px shown around the post
const PLACE_STAGE_W = 340;   // on-screen stage width

/** Move / resize the featured photo under the ad, so the banner doesn't
 *  cover something important. The post keeps the photo's own shape; the
 *  ad + legend layer (fetched once per ad/style as a transparent PNG) is
 *  laid over it while dragging. Show preview renders the real thing. */
function SponsoredPhotoPlacement({ article, photoUrl, photoPath, adId, adStyle, placement, setPlacement }) {
  const [open, setOpen] = useState(!!placement);
  const [overlayUrl, setOverlayUrl] = useState(null);
  // The photo the ad goes on: the post's first photo (featured image
  // unless the editor reordered).
  const raw = photoUrl || article.generated_image_url || "";
  const src = raw && !raw.startsWith("http") ? `${BACKEND_BASE}${raw}` : raw;
  const aspect = useImageAspect(open ? src : "");
  useEffect(() => {
    if (!open || !adId) return undefined;
    let cancelled = false;
    let url = null;
    api
      .fetchFacebookAdPreview(article.id, {
        ad_id: adId, style: adStyle, overlay_only: true, image_path: photoPath || undefined,
      })
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setOverlayUrl(url);
      })
      .catch(() => {
        if (!cancelled) setOverlayUrl(null);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [open, article.id, adId, adStyle, photoPath]);

  if (!open) {
    return (
      <button
        type="button"
        data-testid="fb-share-photo-place-open"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-[var(--border)] hover:border-[var(--ink)]"
      >
        <Move className="w-3.5 h-3.5" />{" "}{tr("Move / resize photo")}</button>
    );
  }
  const W = PLACE_W;
  const H = aspect ? PLACE_W / aspect : 0;
  const rect = placement
    ? manualRect(placement, { width: W }, aspect)
    : { left: 0, top: 0, width: W, height: H };
  return (
    <div data-testid="fb-share-photo-place">
      <div className="flex flex-wrap items-center gap-2 mb-1.5">
        {trRich("<0>Photo position</0>", null, [<span className="overline" />])}
        <button
          type="button"
          data-testid="fb-share-photo-place-reset"
          onClick={() => setPlacement(null)}
          disabled={!placement}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[var(--border)] hover:border-[var(--ink)] disabled:opacity-50"
        >
          <RotateCcw className="w-3 h-3" />{" "}{tr("Reset")}</button>
      </div>
      {!aspect ? (
        <div className="text-[11px] text-[var(--ink-muted)] inline-flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" />{" "}{tr("Loading photo…")}</div>
      ) : (
        <div className="overflow-x-auto">
        <PhotoStage
          contentW={W}
          contentH={H}
          marginX={PLACE_MARGIN}
          marginY={PLACE_MARGIN}
          scale={PLACE_STAGE_W / (W + 2 * PLACE_MARGIN)}
          rect={rect}
          src={src}
          unitW={W}
          minW={W * MIN_ZOOM}
          maxW={W * MAX_ZOOM}
          viewports={[{
            key: "post", left: 0, top: 0, width: W, height: H,
            node: (
              <div style={{ position: "relative", width: W, height: H, overflow: "hidden", backgroundColor: "#0B0B0B" }}>
                <img
                  src={src}
                  alt=""
                  style={{
                    position: "absolute", left: rect.left, top: rect.top,
                    width: rect.width, height: rect.height, maxWidth: "none", maxHeight: "none",
                  }}
                />
                {overlayUrl && (
                  <img
                    src={overlayUrl}
                    alt=""
                    style={{ position: "absolute", inset: 0, width: W, height: H, maxWidth: "none" }}
                  />
                )}
              </div>
            ),
          }]}
          testid="fb-share-photo-stage"
          onChange={(nr) => setPlacement(rectToPlacement(nr, W))}
        />
        </div>
      )}
      <p className="text-[11px] text-[var(--ink-muted)] mt-1">{tr("Drag a corner to resize (the shape stays), drag the photo to move it, so the ad doesn't cover anything important. The post keeps the photo's shape; uncovered areas are filled dark. Only the Facebook copy changes — WordPress is untouched.")}</p>
    </div>
  );
}

function SponsorAdPicker({
  ads, adId, setAdId, adStyle, setAdStyle,
  previewUrl, previewMutation,
  article, photoUrl, photoPath, multiPhoto, photoPlacement, setPhotoPlacement,
}) {
  return (
    <div className="border border-dashed border-[var(--border)] p-3 bg-[var(--secondary)] space-y-3">
      <div className="flex items-center gap-2">
        <Megaphone className="w-3.5 h-3.5 text-[var(--ink-muted)]" />
        {trRich("<0>Sponsor ad overlay</0><1>{value}</1>", { value: multiPhoto
            ? tr("(optional — goes on the first photo only; WP image is untouched)")
            : tr("(optional — WP image is untouched)") }, [<span className="overline" />, <span className="text-[11px] text-[var(--ink-muted)]" />])}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select
          data-testid="fb-share-ad-select"
          value={adId}
          onChange={(e) => setAdId(e.target.value)}
          className="appearance-none pl-3 pr-8 py-1.5 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
        >
          <option value="">{tr("— No ad —")}</option>
          {ads.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
        <a
          href="/sponsor-ads"
          className="text-[11px] text-[var(--accent)] hover:underline"
        >
          {ads.length === 0
            ? tr("Create your first ad →")
            : tr("Edit styles on /sponsor-ads →")}
        </a>
      </div>

      {adId && (
        <>
          <div>
            <div className="overline mb-1.5">{tr("Style")}</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {AD_STYLES.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  data-testid={`fb-share-ad-style-${s.key}`}
                  onClick={() => setAdStyle(s.key)}
                  title={s.hint}
                  className={
                    "text-left text-xs px-2.5 py-1.5 border " +
                    (adStyle === s.key
                      ? "border-[#1877F2] bg-[#1877F2] text-white"
                      : "border-[var(--border)] bg-white hover:border-[var(--ink)]")
                  }
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <SponsoredPhotoPlacement
            key={photoPath}
            article={article}
            photoUrl={photoUrl}
            photoPath={photoPath}
            adId={adId}
            adStyle={adStyle}
            placement={photoPlacement}
            setPlacement={setPhotoPlacement}
          />

          {/* Preview button + inline image — server renders against the
              post's REAL first photo, so editors see exactly what
              Facebook will get. Layout comes from the ad's saved
              styles; there's nothing to configure here. */}
          <div>
            <button
              type="button"
              data-testid="fb-share-ad-preview-btn"
              onClick={() => previewMutation.mutate()}
              disabled={previewMutation.isPending || !adId}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-[var(--border)] hover:border-[var(--ink)] disabled:opacity-50"
            >
              {previewMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Eye className="w-3.5 h-3.5" />
              )}
              {previewUrl ? tr("Refresh preview") : tr("Show preview")}
            </button>
            {previewUrl && (
              <div
                data-testid="fb-share-ad-preview-wrap"
                className="mt-3 border border-[var(--border)] bg-white"
              >
                <img
                  data-testid="fb-share-ad-preview-img"
                  src={previewUrl}
                  alt={tr("Facebook post preview with sponsor ad")}
                  className="w-full h-auto block"
                />
                <div className="px-2 py-1 text-[10px] text-[var(--ink-muted)] font-mono-display border-t border-[var(--border)]">{tr("Exactly what Facebook will see. Disclosure legend auto-repositioned so it doesn't collide with the ad.")}</div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
